import { Button } from "$components/button/button";
import { DialogBody } from "$components/dialog/dialog-body";
import { DialogContainer } from "$components/dialog/dialog-container";
import { DialogFooter } from "$components/dialog/dialog-footer";
import { WarningIcon } from "$components/icons";
import { DatePicker } from "$components/inputs";
import { SelectField, SubmitButton } from "$components/rhf-fields";
import { ProductOriginBadge } from "$routes/inventory/_lib/product-origin-badge";
import { RHFTextField } from "$routes/inventory/_lib/rhf-text-field";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, Typography } from "@material-ui/core";
import { styled } from "@material-ui/core/styles";
import { useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useValidationSchema } from "./edit-lot-form-validation";
import useReasonTypes from "./use-reason-types";

interface EditLotProps {
  onClose(): void;
  isOpen: boolean;
  lot: {
    expiryDate: Date;
    id: string;
    name: string;
    quantity: number;
    skuId: string;
    externalReferences?: ReadonlyArray<{ origin: string }>;
  };
  handleLotEditSubmit: (data: FormData) => void;
}

interface FormData {
  id: string;
  quantity: number;
  expiryDate: Date;
  reasonText: string;
  reasonCategory: string;
}

const StyledWarningIcon = styled(WarningIcon)(({ theme }) => ({
  color: theme.palette.warning.main,
  marginRight: theme.spacing(1),
}));

function EditLotModal(props: EditLotProps) {
  const { onClose, isOpen, lot, handleLotEditSubmit } = props;
  const { formatMessage } = useIntl();
  const reasonTypes = useReasonTypes();
  const validationSchema = useValidationSchema();

  const formMethods = useForm<FormData>({
    mode: "onTouched",
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    const defaults = {
      id: lot?.id,
      quantity: lot?.quantity,
      expiryDate: lot?.expiryDate,
      reasonText: "",
      reasonCategory: null,
    };
    formMethods.reset(defaults);
  }, [lot, formMethods.reset]);

  if (!isOpen) {
    return null;
  }

  const quantityField = formMethods.watch("quantity");
  const expiryField = formMethods.watch("expiryDate");
  const reasonCategory = formMethods.watch("reasonCategory");
  const hasChangedLotData =
    quantityField !== lot?.quantity || expiryField !== lot?.expiryDate;
  const isValid = formMethods.formState.isValid && hasChangedLotData;

  return (
    <DialogContainer
      open={isOpen}
      onClose={onClose}
      data-testid="edit-lot-modal"
    >
      <FormProvider {...formMethods}>
        <form
          aria-label="form"
          onSubmit={formMethods.handleSubmit(handleLotEditSubmit)}
        >
          <DialogBody>
            <Grid container>
              <Grid item xs={12}>
                <Box pb={2} display="flex" alignItems="center" gridGap={8}>
                  <StyledWarningIcon viewBox="0 0 32 32" fontSize="large" />
                  <Typography variant="h4" component="h1">
                    {lot?.name}
                  </Typography>
                  <ProductOriginBadge
                    externalReferences={lot?.externalReferences}
                  />
                </Box>
                <Box pb={1}>
                  <Typography variant="caption">
                    <FormattedMessage
                      id="inventory.modal.editLot.field-quantityHelpText"
                      defaultMessage="Quantity or expiry date must changed to submit an update"
                    />
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <RHFTextField
                  name="quantity"
                  label={formatMessage({
                    id: "inventory.modal.editLot.field-quantity",
                    defaultMessage: "Quantity",
                  })}
                  data-testid="lot-quantity-field"
                  type="number"
                  required
                  style={{ width: "100%" }}
                />
              </Grid>
              <Grid item xs={4}>
                <Controller
                  control={formMethods.control}
                  name="expiryDate"
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <DatePicker
                      label={formatMessage({
                        id: "inventory.modal.editLot.field-expiryDate",
                        defaultMessage: "Expiry Date",
                      })}
                      data-testid="lot-expiryDate-field"
                      views={["year", "month"]}
                      disablePast
                      format="MM / yyyy"
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value}
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item xs={4}>
                <SelectField
                  name="reasonCategory"
                  label={formatMessage({
                    id: "inventory.modal.editLot.field-reasonCategory",
                    defaultMessage: "Type of change",
                  })}
                  options={reasonTypes}
                  fullWidth
                  customWidth="100%"
                  required
                  selectProps={{
                    "data-testid": "lot-reasonType-select",
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <RHFTextField
                  name="reasonText"
                  label={formatMessage({
                    id: "inventory.modal.editLot.field-reason",
                    defaultMessage: "Reason for change",
                  })}
                  data-testid="lot-reasonText-field"
                  multiline
                  rows={3}
                  fullWidth
                  required={reasonCategory === "OTHER"}
                />
              </Grid>
            </Grid>
          </DialogBody>
          <DialogFooter>
            <Button color="secondary" onClick={onClose}>
              <FormattedMessage
                id="inventory.modal.editLot.cancel"
                defaultMessage="Cancel"
              />
            </Button>
            <SubmitButton
              disabled={!isValid}
              data-testid="edit-lot-submit-button"
            >
              <FormattedMessage
                id="inventory.modal.editLot.submit"
                defaultMessage="Update Lot"
              />
            </SubmitButton>
          </DialogFooter>
        </form>
      </FormProvider>
    </DialogContainer>
  );
}

export { EditLotModal };
